import "@/sim/sim.css";
import { motion } from "framer-motion";
import {
  Radar as RadarIcon, Play, ArrowRight, ArrowUpRight, Target, Gauge, Eye, ListChecks,
  Building2, ShoppingBag, Clock, Layers, TrendingUp, CheckCircle2, ShieldCheck,
  Search, ScanLine, Cpu, Briefcase, Smartphone, Zap, Star, Tag, Crown,
} from "lucide-react";
import { simGoogleLogin } from "@/sim/lib";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5, delay: d },
});

const BENEFITS = [
  { icon: Target, title: "Catch conversion killers pre-launch", body: "Surface the friction, confusion and trust gaps that make real visitors bounce — before you spend a dollar on traffic." },
  { icon: Eye, title: "Predictive attention heatmaps", body: "See where synthetic eyes land, where thumbs tap, and exactly where users hit friction on every viewport." },
  { icon: ListChecks, title: "Prioritized remediation backlog", body: "Every objection comes with a concrete fix and an estimated conversion-rate lift, ranked by severity." },
  { icon: Layers, title: "10 distinct behavioral personas", body: "Skeptics, skimmers, bargain-hunters and executives — each with real cognitive biases and risk tolerance." },
  { icon: Gauge, title: "A single Product Health score", body: "Clarity, usability, trust, value and accessibility distilled into one benchmarked number you can track." },
  { icon: ShieldCheck, title: "Any URL — even staging", body: "Point it at production or a password-protected pre-prod environment and get a verdict in under 75 seconds." },
];

const STEPS = [
  { n: "01", icon: Play, title: "Drop any URL", body: "Paste a landing page, pricing page or full product. Pick a B2B or B2C audience and a viewport." },
  { n: "02", icon: RadarIcon, title: "The swarm evaluates", body: "Ten vision-driven AI agents render and scrutinize your page in parallel, each through its own bias." },
  { n: "03", icon: TrendingUp, title: "Get a scorecard + fixes", body: "A health score, attention heatmap, per-persona verdicts and a ranked backlog of high-leverage fixes." },
];

const AGENTS = [
  { icon: Search, name: "The Skeptic" }, { icon: ScanLine, name: "The Skimmer" },
  { icon: Cpu, name: "The Architect" }, { icon: Briefcase, name: "The Executive" },
  { icon: Smartphone, name: "Mobile Reviewer" }, { icon: ShieldCheck, name: "The Guardian" },
  { icon: Tag, name: "Bargain Stalker" }, { icon: Zap, name: "Impulse Explorer" },
  { icon: Star, name: "Social Proof Seeker" }, { icon: Crown, name: "Lifestyle Aspirant" },
];

const RADAR = [
  { k: "Clarity", v: 82 }, { k: "Usability", v: 74 }, { k: "Trust", v: 68 },
  { k: "Value", v: 71 }, { k: "Accessibility", v: 63 },
];

function PublicNav() {
  return (
    <header className="sim-glass sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="sim-landing-logo">
          <div className="w-7 h-7 rounded-md bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center">
            <RadarIcon className="w-4 h-4 text-[#00F0FF]" />
          </div>
          <span className="sim-head text-[15px] font-extrabold text-[#F5F5F5] tracking-tight">
            sim<span className="text-[#00F0FF]">.</span>roas-ai
          </span>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/login"
            data-testid="sim-landing-roas-link"
            className="sim-mono text-[11px] text-[#C2C5CE] hover:text-[#00F0FF] transition-colors hidden sm:inline-flex items-center gap-1"
          >
            ROAS-AI <ArrowUpRight className="w-3 h-3" />
          </a>
          <button
            data-testid="sim-landing-signin-top"
            onClick={simGoogleLogin}
            className="sim-mono text-[11px] text-[#0A0A0A] bg-[#00F0FF] hover:bg-[#33f3ff] font-bold px-3.5 py-2 rounded transition-colors flex items-center gap-1.5"
          >
            LAUNCH <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </header>
  );
}

export default function SimLanding() {
  return (
    <div className="sim-root" data-testid="sim-landing">
      <PublicNav />

      {/* Hero */}
      <section className="sim-grid-bg">
        <div className="max-w-6xl mx-auto px-5 pt-16 pb-14 text-center">
          <motion.div {...fade(0)} className="sim-mono inline-flex items-center gap-2 text-[11px] text-[#00F0FF] border border-[#00F0FF]/25 bg-[#00F0FF]/5 rounded-full px-3 py-1 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-pulse" /> SYNTHETIC USER TESTING
          </motion.div>
          <motion.h1 {...fade(0.05)} className="sim-head text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#F5F5F5] leading-[1.03] max-w-3xl mx-auto">
            Test your product on<br /><span className="text-[#00F0FF]">10 synthetic users</span> in 75 seconds
          </motion.h1>
          <motion.p {...fade(0.12)} className="text-[#D4D4D8] text-base sm:text-lg mt-5 max-w-2xl mx-auto">
            Drop any website, App Store listing or mobile app binary (.apk / .ipa). An orchestrated swarm of AI agents — each with distinct biases, attention spans and risk tolerance — scrutinizes it and returns an empirical usability scorecard with ranked, revenue-weighted fixes.
          </motion.p>
          <motion.div {...fade(0.18)} className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <button
              data-testid="sim-landing-launch"
              onClick={simGoogleLogin}
              className="sim-tracing-btn h-12 px-7 rounded-lg bg-[#00F0FF] text-[#0A0A0A] font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#33f3ff] transition-colors"
            >
              <Play className="w-4 h-4 fill-current" /> Launch the simulator
            </button>
            <a
              href="#how"
              data-testid="sim-landing-how"
              className="h-12 px-6 rounded-lg bg-white/5 border border-[#27272A] text-[#F5F5F5] font-semibold text-sm flex items-center justify-center gap-2 hover:border-[#3f3f46] transition-colors"
            >
              See how it works <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
          <motion.p {...fade(0.24)} className="sim-mono text-[11px] text-[#9AA0AC] mt-4">
            3 free simulations · then $99/mo or $990/yr (2 months free) · results in ~75s
          </motion.p>
        </div>

        {/* Metric strip */}
        <div className="max-w-5xl mx-auto px-5 pb-16">
          <motion.div {...fade(0.1)} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { v: "10", l: "AI personas / run" },
              { v: "~75s", l: "Avg. completion" },
              { v: "3", l: "Device viewports" },
              { v: "0", l: "Setup required" },
            ].map((m) => (
              <div key={m.l} className="sim-card rounded-xl p-4 text-center">
                <p className="sim-head text-3xl font-extrabold text-[#00F0FF]">{m.v}</p>
                <p className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider mt-1">{m.l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <motion.div {...fade(0)} className="max-w-2xl mb-10">
          <p className="sim-mono text-[11px] text-[#00F0FF] uppercase tracking-wider mb-2">Why teams use it</p>
          <h2 className="sim-head text-3xl sm:text-4xl font-extrabold text-[#F5F5F5]">Real user research, minus the weeks of recruiting</h2>
          <p className="text-[#C2C5CE] text-base mt-3">Stop guessing why visitors drop off. Get the critique of a full UX panel on demand, as many times as you ship.</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} {...fade(i * 0.05)} data-testid={`sim-benefit-${i}`}
              className="sim-card-elevated rounded-xl p-5 hover:border-[#00F0FF]/40 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/25 flex items-center justify-center mb-4">
                <b.icon className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <h3 className="sim-head text-lg font-bold text-[#F5F5F5]">{b.title}</h3>
              <p className="text-[#C2C5CE] text-sm mt-2 leading-relaxed">{b.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="sim-grid-bg border-y border-[#1c1c1f]">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <motion.div {...fade(0)} className="text-center max-w-2xl mx-auto mb-12">
            <p className="sim-mono text-[11px] text-[#00F0FF] uppercase tracking-wider mb-2">How it works</p>
            <h2 className="sim-head text-3xl sm:text-4xl font-extrabold text-[#F5F5F5]">Three steps, zero setup</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fade(i * 0.08)} data-testid={`sim-step-${i}`} className="sim-card-elevated rounded-xl p-6 relative">
                <span className="sim-mono text-5xl font-extrabold text-[#1f1f22] absolute top-4 right-5 select-none">{s.n}</span>
                <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/25 flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <h3 className="sim-head text-lg font-bold text-[#F5F5F5]">{s.title}</h3>
                <p className="text-[#C2C5CE] text-sm mt-2 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Persona swarm + sample scorecard */}
      <section className="max-w-6xl mx-auto px-5 py-16 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div {...fade(0)}>
          <p className="sim-mono text-[11px] text-[#00F0FF] uppercase tracking-wider mb-2">The swarm</p>
          <h2 className="sim-head text-3xl sm:text-4xl font-extrabold text-[#F5F5F5]">A panel of biased, opinionated users</h2>
          <p className="text-[#C2C5CE] text-base mt-3 mb-6">
            Switch between a B2B software panel and a B2C consumer panel. Each agent role-plays a distinct archetype and reports what made them convert, hesitate or leave.
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {AGENTS.map((a, i) => (
              <div key={a.name} data-testid={`sim-agent-${i}`} className="sim-card rounded-lg px-3 py-2.5 flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-md bg-white/5 flex items-center justify-center shrink-0">
                  <a.icon className="w-3.5 h-3.5 text-[#00F0FF]" />
                </div>
                <span className="text-[#D4D4D8] text-sm truncate">{a.name}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-5">
            <span className="flex items-center gap-1.5 sim-mono text-[11px] text-[#C2C5CE]"><Building2 className="w-3.5 h-3.5 text-[#00F0FF]" /> B2B Software</span>
            <span className="flex items-center gap-1.5 sim-mono text-[11px] text-[#C2C5CE]"><ShoppingBag className="w-3.5 h-3.5 text-[#007AFF]" /> B2C Consumer</span>
          </div>
        </motion.div>

        {/* Sample scorecard mock */}
        <motion.div {...fade(0.1)} className="sim-card-elevated rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <span className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider">Sample scorecard</span>
            <span className="sim-mono text-[10px] text-[#10B981] flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> COMPLETED</span>
          </div>
          <div className="flex items-end gap-5 mb-6">
            <div>
              <p className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider">Product Health</p>
              <p className="sim-head text-6xl font-extrabold text-[#00F0FF] leading-none mt-1">74</p>
              <p className="sim-mono text-[11px] text-[#10B981] mt-1">Strong</p>
            </div>
            <div className="flex-1 space-y-2">
              {RADAR.map((r) => (
                <div key={r.k}>
                  <div className="flex justify-between sim-mono text-[10px] text-[#C2C5CE] mb-1">
                    <span>{r.k}</span><span className="text-[#F5F5F5]">{r.v}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#0A0A0A] overflow-hidden">
                    <div className="h-full rounded-full bg-[#00F0FF]" style={{ width: `${r.v}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { l: "Approved", v: 6, c: "#10B981" },
              { l: "Hesitant", v: 3, c: "#F59E0B" },
              { l: "Dropped", v: 1, c: "#EF4444" },
            ].map((v) => (
              <div key={v.l} className="sim-card rounded-lg py-2.5">
                <p className="sim-head text-xl font-bold" style={{ color: v.c }}>{v.v}</p>
                <p className="sim-mono text-[9px] text-[#C2C5CE] uppercase tracking-wider">{v.l}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 sim-mono text-[11px] text-[#9AA0AC]">
            <Clock className="w-3 h-3" /> Time-to-first-comprehension · 3.8s
          </div>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="sim-grid-bg border-t border-[#1c1c1f]">
        <div className="max-w-3xl mx-auto px-5 py-20 text-center">
          <motion.h2 {...fade(0)} className="sim-head text-3xl sm:text-5xl font-extrabold text-[#F5F5F5]">
            See your product through<br /><span className="text-[#00F0FF]">10 pairs of eyes</span>
          </motion.h2>
          <motion.p {...fade(0.08)} className="text-[#C2C5CE] text-base mt-4 max-w-xl mx-auto">
            Sign in with Google and run your first simulation in under two minutes.
          </motion.p>
          <motion.button
            {...fade(0.14)}
            data-testid="sim-landing-cta"
            onClick={simGoogleLogin}
            className="sim-tracing-btn h-12 px-8 rounded-lg bg-[#00F0FF] text-[#0A0A0A] font-bold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#33f3ff] transition-colors mt-8"
          >
            <Play className="w-4 h-4 fill-current" /> Launch the simulator
          </motion.button>
        </div>
      </section>

      <footer className="border-t border-[#1c1c1f]">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <RadarIcon className="w-4 h-4 text-[#00F0FF]" />
            <span className="sim-mono text-[11px] text-[#C2C5CE]">sim.roas-ai · Synthetic Audience Simulator</span>
          </div>
          <a
            href="/login"
            data-testid="sim-landing-footer-roas"
            className="sim-mono text-[11px] text-[#8E929C] hover:text-[#00F0FF] transition-colors inline-flex items-center gap-1"
          >
            Powered by ROAS-AI <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>
      </footer>
    </div>
  );
}
